import { z } from 'zod';
import { assetListQuerySchema, holderInputSchema } from './asset.js';
import { LABEL_FORMATS, DEFAULT_LABEL_FORMAT } from '../labels.js';

/**
 * Admin- und Automatisierungsschnittstelle.
 *
 * Jede schreibende Operation kennt `dry_run` und liefert dann die betroffenen
 * Datensätze zurück, ohne etwas zu ändern — Voraussetzung dafür, dass
 * Massenänderungen gefahrlos über MCP oder Skript laufen können.
 */

export const snipeImportSchema = z.object({
  dry_run: z.boolean().default(false),
  /** Einzelne Bereiche überspringen, etwa für einen reinen Delta-Lauf. */
  include: z
    .array(z.enum(['stammdaten', 'employees', 'assets', 'movements']))
    .default(['stammdaten', 'employees', 'assets', 'movements']),
  /** Vorhandene Datensätze nicht überschreiben, nur fehlende ergänzen. */
  only_new: z.boolean().default(false),
});

export const snipeImportResultSchema = z.object({
  dry_run: z.boolean(),
  started_at: z.string(),
  finished_at: z.string(),
  duration_ms: z.number().int(),
  sections: z.array(
    z.object({
      name: z.string(),
      fetched: z.number().int(),
      created: z.number().int(),
      updated: z.number().int(),
      skipped: z.number().int(),
    }),
  ),
  warnings: z.array(z.string()),
});

/** Massenänderung: entweder über eine ID-Liste oder über einen Filter. */
const targetSelectorSchema = z
  .object({
    asset_ids: z.array(z.number().int().positive()).max(1000).optional(),
    filter: assetListQuerySchema.partial().optional(),
  })
  .refine((v) => Boolean(v.asset_ids?.length) || Boolean(v.filter), {
    message: 'Entweder asset_ids oder filter angeben',
  });

export const adminBulkUpdateSchema = z.object({
  select: targetSelectorSchema,
  set: z
    .object({
      status_id: z.number().int().positive().optional(),
      model_id: z.number().int().positive().optional(),
      home_location_id: z.number().int().positive().nullable().optional(),
      supplier_id: z.number().int().positive().nullable().optional(),
      order_number: z.string().trim().max(120).nullable().optional(),
      datev_id: z.string().trim().max(60).nullable().optional(),
      notes: z.string().trim().max(4000).nullable().optional(),
    })
    .refine((v) => Object.keys(v).length > 0, { message: 'Keine Änderung angegeben' }),
  note: z.string().trim().max(1000).optional(),
  dry_run: z.boolean().default(true),
});

export const adminReassignSchema = z.object({
  select: targetSelectorSchema,
  to: holderInputSchema,
  note: z.string().trim().max(1000).optional(),
  dry_run: z.boolean().default(true),
});

/** Asset-Tags normalisieren oder neu vergeben. */
export const adminRenumberSchema = z.object({
  select: targetSelectorSchema.optional(),
  mode: z.enum(['normalize', 'sequential']).default('normalize'),
  /** Nur bei `sequential`: Startnummer. */
  start_at: z.number().int().positive().optional(),
  dry_run: z.boolean().default(true),
});

export const adminModelMergeSchema = z.object({
  source_model_ids: z.array(z.number().int().positive()).min(1).max(50),
  target_model_id: z.number().int().positive(),
  /** Quellmodelle nach dem Umhängen löschen. */
  delete_sources: z.boolean().default(true),
  dry_run: z.boolean().default(true),
});

export const adminAffectedAssetsSchema = z.object({
  dry_run: z.boolean(),
  affected: z.number().int(),
  assets: z.array(
    z.object({
      id: z.number().int(),
      asset_tag: z.string(),
      serial: z.string(),
      model_name: z.string().nullable(),
      before: z.record(z.string(), z.unknown()).optional(),
      after: z.record(z.string(), z.unknown()).optional(),
    }),
  ),
});

export const auditListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
  entity: z.string().trim().max(60).optional(),
  entity_id: z.coerce.number().int().positive().optional(),
  actor_user_id: z.coerce.number().int().positive().optional(),
  action: z.string().trim().max(60).optional(),
});

export const labelPrintSchema = z.object({
  asset_ids: z.array(z.number().int().positive()).min(1).max(200),
  format: z
    .enum(LABEL_FORMATS.map((f) => f.id) as [string, ...string[]])
    .default(DEFAULT_LABEL_FORMAT),
  /** Modellbezeichnung mitdrucken, sofern das Format Platz bietet. */
  show_model: z.boolean().default(true),
  /**
   * Seriennummer mitdrucken, sofern sie gepflegt ist und das Format Platz hat.
   * Vorgabe an: Beim Wiederfinden eines Geräts ist sie oft die einzige Angabe,
   * die auch auf dem Gerät selbst steht.
   */
  show_serial: z.boolean().default(true),
  /** `label_printed_at` setzen. Bei Nachdruck abschaltbar. */
  mark_printed: z.boolean().default(true),
});
export type LabelPrint = z.infer<typeof labelPrintSchema>;
